import { ReactNode, useEffect, useRef, useState } from 'react';
import { Flex, Icon, Input, Text } from '@chakra-ui/react';
import { Pencil } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { testIds as blockNodeTestIds } from './FlowEditorBlockNode/testIds';

// Shared text styling for the label whether it renders read-only or editable,
// so the node title looks identical in both modes.
const labelTextStyles = {
  fontSize: 'sm',
  fontWeight: 'semibold',
  color: 'gray.600',
};

// -- RenamableLabel ------------------------------------------------------------
// The node title. Read-only (and the node's drag handle) until renaming is
// triggered from the actions menu, at which point it swaps to a focused text
// input. Enter or blur commits via onRename, Escape reverts; either way it tells
// the parent to leave rename mode.
//
// Focus is the fiddly part: the actions menu returns focus to its trigger button
// as it closes, which happens a tick or two after Rename is clicked. If we focus
// the input immediately it gets blurred straight back out. So we focus on a short
// timeout (after the menu's focus hand-off) and only arm the commit-on-blur once
// the field is genuinely focused, so the menu's transient blur is ignored.
function RenamableLabel({
  label,
  isRenaming,
  onRename,
  onRenameEnd,
  onStartRename,
}: {
  label?: string;
  isRenaming: boolean;
  onRename: (label: string) => void;
  onRenameEnd: () => void;
  onStartRename?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(label ?? '');
  const hasFocused = useRef(false);

  // Re-seed the draft whenever the label changes externally. The label only
  // changes mid-edit through undo/redo (the user's own keystrokes update draft,
  // not label, until they commit), so this lets a restored value win over a
  // stale draft instead of the next blur committing the stale draft back over
  // the restore. Typing isn't affected: while the user edits, label is
  // unchanged, so this effect doesn't fire.
  useEffect(() => {
    setDraft(label ?? '');
  }, [label]);

  useEffect(() => {
    if (!isRenaming) {
      hasFocused.current = false;
      return;
    }
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
      hasFocused.current = true;
    }, 50);
    return () => clearTimeout(timer);
  }, [isRenaming]);

  if (!isRenaming) {
    return (
      <Text
        {...labelTextStyles}
        flex="1"
        minW={0}
        className="drag-handle"
        noOfLines={1}
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        // Double-click the title to rename, the same gesture Figma/Miro use.
        // A double-click without pointer movement never starts a node drag, so
        // this co-exists with the title doubling as the drag handle.
        onDoubleClick={onStartRename}
      >
        {label}
      </Text>
    );
  }

  const commit = () => {
    onRename(draft);
    onRenameEnd();
  };

  return (
    <Flex align="center" gap={1} flex="1" minW={0} px={1} borderRadius="md" bg="bg.muted">
      <Input
        ref={inputRef}
        {...labelTextStyles}
        flex="1"
        minW={0}
        h="auto"
        variant="unstyled"
        _focusVisible={{ outline: 'none', boxShadow: 'none' }}
        data-testid={blockNodeTestIds.nodeTitleInput}
        value={draft}
        onChange={(ev) => setDraft(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') {
            ev.preventDefault();
            commit();
          } else if (ev.key === 'Escape') {
            ev.preventDefault();
            onRenameEnd();
          }
        }}
        onBlur={() => {
          // Ignore the menu's transient focus-return blur; only commit once the
          // user has actually been editing the field.
          if (hasFocused.current) {
            commit();
          }
        }}
      />
      <Icon as={Pencil} boxSize={iconSize.xs} color="fg.muted" flexShrink={0} />
    </Flex>
  );
}

// -- BlockNodeHeader -----------------------------------------------------------
// The title bar shared by the live and static flow nodes: the node label on the
// left and an actions slot (the duplicate/delete menu) on the right. Kept as one
// component so the live canvas node and the Storybook static node render an
// identical header. The label carries the `drag-handle` class so React Flow
// drags the node from the title, not the actions button.
//
// When `onRename` is given (the live editor) the label can be renamed in place,
// driven by `isRenaming` (toggled from the actions menu). Without it (the static
// node and stories) the label is always read-only text.
export function BlockNodeHeader({
  label,
  actions,
  isRenaming = false,
  onRename,
  onRenameEnd,
  onStartRename,
}: {
  label?: string;
  actions?: ReactNode;
  isRenaming?: boolean;
  onRename?: (label: string) => void;
  onRenameEnd?: () => void;
  onStartRename?: () => void;
}) {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={2}
      pl={3}
      pr={1}
      py={2}
      borderTopRadius="lg"
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      {onRename && onRenameEnd ? (
        <RenamableLabel
          label={label}
          isRenaming={isRenaming}
          onRename={onRename}
          onRenameEnd={onRenameEnd}
          onStartRename={onStartRename}
        />
      ) : (
        <Text
          {...labelTextStyles}
          flex="1"
          minW={0}
          className="drag-handle"
          noOfLines={1}
          cursor="grab"
          _active={{ cursor: 'grabbing' }}
        >
          {label}
        </Text>
      )}
      {actions}
    </Flex>
  );
}
